import messages from "messages";
import { Form, Col, Row } from "react-bootstrap";
import { ReactNode, useCallback } from "react";
// components
import CustomSelect from "components/custom-select/CustomSelect";
import Checkbox from "components/checkbox/Checkbox";
import AddExistingContact from "../add-existing-contact";
import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
// utils
import getClasses from "utils/getClasses";
// styles
import styles from "./ContactInfoEditor.module.scss";
// types

import { BILLING_TYPES } from "components/patient-editor/constants/billingTypes";
import type { PatientForPatientEditor } from "components/patient-editor/types";
// constants
import { RELATIONSHIPS_TO_PATIENT_OPTIONS } from "components/utility/option-constants";
import { AmplitudePageName, track } from "utils/amplitude";

type Props = {
  value: Partial<PatientForPatientEditor>;
  onChange: (arg: Partial<PatientForPatientEditor>) => void;
  onBillingContactChange: (arg: string) => void;
  onGuardianTypeChange: (arg: string) => void;
  billingContact: string;
  billingType: string;
  setBillingType: (arg: string) => void;
  label?: ReactNode;
  labelClass?: string;
  className?: string;
  amplitudePageName: AmplitudePageName;
  amplitudeComponentName?: string;
};

const AMPLITUDE_COMPONENT_NAME = "contact-info-editor";

/**
 * renders a stateless UI for contact info creation and edit
 */
const ContactInfoEditor = ({
  value,
  onChange,
  onBillingContactChange,
  onGuardianTypeChange,
  billingContact,
  billingType,
  setBillingType,
  label = messages.contact,
  labelClass,
  className,
  amplitudePageName,
  amplitudeComponentName,
}: Props) => {
  const componentBreadCrumbs = amplitudeComponentName
    ? [amplitudeComponentName, AMPLITUDE_COMPONENT_NAME]
    : [AMPLITUDE_COMPONENT_NAME];

  const onInputChange = useCallback(
    (event) => {
      const val = event.currentTarget.value;
      const name = event.currentTarget.name;
      onChange({ ...value, [name]: val });
    },
    [onChange, value],
  );

  return (
    <div className={className}>
      <div>
        <div className={labelClass}>
          {label}
          <AddExistingContact
            onApply={(e) => {
              onChange(e);
              track(
                amplitudePageName,
                [...componentBreadCrumbs, "add-existing-contact"],
                "click",
              );
            }}
          />
        </div>
        <Row className={"mt-3"}>
          <Form.Group as={Col} md="6">
            <Form.Label className={styles["contact-info-editor__label"]}>
              First Name <span className="required">*</span>
            </Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Enter First Name"
              name={"first_name"}
              value={value?.first_name || ""}
              onChange={onInputChange}
              onFocus={() => {
                track(
                  amplitudePageName,
                  [...componentBreadCrumbs, "first-name-field"],
                  "focus",
                );
              }}
            />
          </Form.Group>
          <Form.Group as={Col} md="6">
            <Form.Label className={styles["contact-info-editor__label"]}>
              Last Name <span className="required">*</span>
            </Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Enter Last Name"
              name="last_name"
              value={value?.last_name || ""}
              onChange={onInputChange}
              onFocus={() => {
                track(
                  amplitudePageName,
                  [...componentBreadCrumbs, "last-name-field"],
                  "focus",
                );
              }}
            />
          </Form.Group>
        </Row>
        <Form.Group>
          <Form.Label className={styles["contact-info-editor__label"]}>
            Email Address
          </Form.Label>
          <Form.Control
            name="email"
            type="text"
            value={value?.email || ""}
            onChange={onInputChange}
            placeholder={"Enter Email Address"}
            onFocus={() => {
              track(
                amplitudePageName,
                [...componentBreadCrumbs, "email-field"],
                "focus",
              );
            }}
          />
        </Form.Group>
        <Checkbox
          className={"ml-0 my-3"}
          variant={"rectangle"}
          label={"Send email notifications related to the client"}
          disabled={true}
        />

        <Form.Label className={styles["contact-info-editor__label"]}>
          Relationship to patient <span className="required">*</span>
        </Form.Label>
        <Row>
          <Form.Group className={"mb-0"} as={Col} md="6">
            <CustomSelect
              options={RELATIONSHIPS_TO_PATIENT_OPTIONS}
              required
              defaultValue={{ value: "Parent", label: "Parent" }}
              onChange={(el) => {
                onGuardianTypeChange(el.value.toLowerCase());
                track(
                  amplitudePageName,
                  [
                    ...componentBreadCrumbs,
                    `${el.id}-relation-to-patient-option`,
                  ],
                  "click",
                );
              }}
              value={RELATIONSHIPS_TO_PATIENT_OPTIONS.find(
                (option) =>
                  option.value.toLowerCase() === value?.guardian_relationship,
              )}
            />
          </Form.Group>
          <Form.Group
            className={"mb-0 d-flex align-items-center"}
            as={Col}
            md="6"
          >
            <Checkbox
              variant={"rectangle"}
              label={"Emergency Contact"}
              onClick={() => null}
              disabled={true}
            />
          </Form.Group>
        </Row>
        <Checkbox
          className={"mt-3 ml-0"}
          variant={"rectangle"}
          label={"Billing Contact (Payer)"}
          checked={billingContact === "patient2"}
          onChange={(event) => {
            track(
              amplitudePageName,
              [...componentBreadCrumbs, "billing-contact-checkbox"],
              "click",
            );

            if (event.currentTarget.checked) {
              onBillingContactChange("patient2");
            } else {
              onBillingContactChange("");
            }
          }}
        />
        {billingContact === "patient2" && (
          <div>
            <hr />
            <div
              className={getClasses(
                "mb-3",
                styles["contact-info-editor__section-header"],
              )}
              id={"billing type"}
            >
              {messages.billingType}
            </div>

            <div
              className={styles["contact-info-editor__radio-group"]}
              role="radiogroup"
              aria-labelledby={messages.billingType}
            >
              {BILLING_TYPES.map((el) => (
                <div
                  className={styles["contact-info-editor__radio-option"]}
                  key={el.value}
                >
                  <CustomSelectionControl
                    type="radio"
                    checked={billingType === el.value}
                    onChange={() => {
                      track(
                        amplitudePageName,
                        [...componentBreadCrumbs, `${el.value}-radio-button`],
                        "click",
                      );
                      setBillingType(el.value);
                    }}
                  />{" "}
                  {el.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactInfoEditor;
